import { useState, type ReactNode } from "react";
import { Activity, BarChart3, BellRing, Bot, BriefcaseBusiness, CircleGauge, ClipboardList, FileSpreadsheet, FlaskConical, KeyRound, LayoutDashboard, Mail, Menu, MessageCircleMore, PackageOpen, PanelLeftClose, PanelLeftOpen, Radar, Settings, Star, Users, X } from "lucide-react";
import { NavLink, useLocation } from "react-router-dom";
import { useAuth } from "../auth/AuthProvider";
import { Badge } from "../components/ui";
import { TopbarTools } from "../components/TopbarTools";
import { WorkspaceTabs } from "../components/WorkspaceTabs";
import { cn, initials } from "../lib/utils";
import type { Role } from "../types/crm";

interface NavigationItem {
  label: string;
  to: string;
  icon: typeof LayoutDashboard;
  roles?: Role[];
}

const navigation: Array<{ label: string; items: NavigationItem[] }> = [
  { label: "业务工作台", items: [
    { label: "总览", to: "/", icon: LayoutDashboard },
    { label: "客户开发", to: "/leads", icon: Radar },
    { label: "客户管理", to: "/customers", icon: Users },
    { label: "产品档案", to: "/products", icon: PackageOpen },
    { label: "待办提醒", to: "/tasks", icon: BellRing },
    { label: "重点客户", to: "/key-customers", icon: Star },
  ] },
  { label: "销售推进", items: [
    { label: "样品管理", to: "/samples", icon: FlaskConical },
    { label: "报价商机", to: "/opportunities", icon: BriefcaseBusiness },
    { label: "WhatsApp", to: "/whatsapp", icon: MessageCircleMore },
    { label: "邮件中心", to: "/email", icon: Mail },
    { label: "数据分析", to: "/reports", icon: BarChart3 },
    { label: "Excel 导入", to: "/imports", icon: FileSpreadsheet, roles: ["super_admin", "manager", "salesperson"] },
  ] },
  { label: "系统管理", items: [
    { label: "免费额度", to: "/usage", icon: CircleGauge },
    { label: "账号与权限", to: "/users", icon: KeyRound, roles: ["super_admin", "manager"] },
    { label: "审计日志", to: "/audit", icon: ClipboardList, roles: ["super_admin", "manager"] },
    { label: "系统设置", to: "/settings", icon: Settings, roles: ["super_admin"] },
  ] },
];

function SidebarContent({ collapsed, onNavigate }: { collapsed: boolean; onNavigate?: () => void }) {
  const { profile, demoMode } = useAuth();
  const role = profile?.role ?? "readonly";
  return <div className="flex h-full flex-col">
    <div className={cn("flex h-20 items-center border-b border-slate-800 px-4", collapsed ? "justify-center" : "gap-3 px-5")}>
      <div className="flex size-10 shrink-0 items-center justify-center rounded-xl bg-blue-500 text-sm font-bold text-white shadow-lg shadow-blue-500/20">YK</div>
      {!collapsed ? <div className="min-w-0"><div className="truncate font-semibold text-white">YKY CRM</div><div className="truncate text-[11px] text-slate-400">智能客户跟进系统</div></div> : null}
    </div>
    <nav className="flex-1 overflow-y-auto px-3 py-5 scrollbar-thin">
      {navigation.map((group) => {
        const allowed = group.items.filter((item) => !item.roles || item.roles.includes(role));
        if (!allowed.length) return null;
        return <div key={group.label} className="mb-6">{!collapsed ? <div className="mb-2 px-3 text-[10px] font-semibold uppercase tracking-[0.16em] text-slate-500">{group.label}</div> : null}<div className="space-y-1">{allowed.map((item) => <NavLink key={item.to} to={item.to} end={item.to === "/"} onClick={onNavigate} title={collapsed ? item.label : undefined} className={({ isActive }) => cn("group flex h-10 items-center rounded-lg text-sm transition", collapsed ? "justify-center" : "gap-3 px-3", isActive ? "bg-blue-500 text-white shadow-md shadow-blue-950/30" : "text-slate-400 hover:bg-slate-800 hover:text-white")}><item.icon className="size-[18px] shrink-0" />{!collapsed ? <span>{item.label}</span> : null}</NavLink>)}</div></div>;
      })}
    </nav>
    <div className="border-t border-slate-800 p-3">
      {demoMode && !collapsed ? <div className="mb-3 rounded-lg border border-amber-400/20 bg-amber-400/10 px-3 py-2 text-[11px] leading-4 text-amber-200"><Bot className="mb-1 size-3.5" />演示模式 · 数据仅在本机</div> : null}
      <div className={cn("flex w-full items-center rounded-lg p-2 text-left", collapsed ? "justify-center" : "gap-3")}>
        <span className="flex size-8 shrink-0 items-center justify-center rounded-lg bg-slate-700 text-xs font-semibold text-white">{initials(profile?.full_name || "YK")}</span>
        {!collapsed ? <span className="min-w-0 flex-1"><span className="block truncate text-xs font-medium text-white">{profile?.full_name || "用户"}</span><span className="block truncate text-[10px] text-slate-500">{profile?.email}</span></span> : null}
      </div>
    </div>
  </div>;
}

export function AppShell({ children }: { children: ReactNode }) {
  const [collapsed, setCollapsed] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const location = useLocation();
  const { demoMode } = useAuth();
  const active = navigation.flatMap((group) => group.items).find((item) => item.to === location.pathname || (item.to !== "/" && location.pathname.startsWith(`${item.to}/`)));

  return <div className="min-h-screen bg-[#f4f6fb]">
    <aside className={cn("fixed inset-y-0 left-0 z-40 hidden bg-slate-950 transition-[width] duration-200 lg:block", collapsed ? "w-[76px]" : "w-[244px]")}><SidebarContent collapsed={collapsed} /></aside>
    {mobileOpen ? <div className="fixed inset-0 z-50 lg:hidden"><button aria-label="关闭菜单" className="absolute inset-0 bg-slate-950/45 backdrop-blur-sm" onClick={() => setMobileOpen(false)} /><aside className="relative h-full w-[280px] bg-slate-950 shadow-2xl"><button aria-label="关闭菜单" className="absolute right-3 top-3 z-10 rounded-lg p-2 text-slate-400 hover:bg-slate-800" onClick={() => setMobileOpen(false)}><X className="size-5" /></button><SidebarContent collapsed={false} onNavigate={() => setMobileOpen(false)} /></aside></div> : null}
    <div className={cn("transition-[padding] duration-200", collapsed ? "lg:pl-[76px]" : "lg:pl-[244px]")}>
      <header className="sticky top-0 z-30 flex h-16 items-center gap-3 border-b border-slate-200/80 bg-white/85 px-4 backdrop-blur-xl sm:px-6">
        <button aria-label="打开菜单" className="rounded-lg p-2 text-slate-500 hover:bg-slate-100 lg:hidden" onClick={() => setMobileOpen(true)}><Menu className="size-5" /></button>
        <button aria-label={collapsed ? "展开侧栏" : "收起侧栏"} className="hidden rounded-lg p-2 text-slate-500 hover:bg-slate-100 lg:block" onClick={() => setCollapsed((value) => !value)}>{collapsed ? <PanelLeftOpen className="size-5" /> : <PanelLeftClose className="size-5" />}</button>
        <div className="h-5 w-px bg-slate-200" />
        <div className="min-w-0"><div className="truncate text-sm font-semibold text-slate-800">{active?.label || "客户详情"}</div><div className="hidden text-[11px] text-slate-400 sm:block">数据按 Asia/Shanghai 显示</div></div>
        <div className="ml-auto flex items-center gap-2">
          {demoMode ? <Badge tone="amber">本地演示</Badge> : <Badge tone="green"><span className="mr-1 size-1.5 rounded-full bg-emerald-500" />安全连接</Badge>}
          <TopbarTools />
        </div>
      </header>
      <WorkspaceTabs />
      <main className="mx-auto max-w-[1600px] p-4 sm:p-6 lg:p-8">{children}</main>
      <footer className="px-6 pb-6 text-center text-[11px] text-slate-400"><div className="inline-flex items-center gap-2"><Activity className="size-3" />AI 默认关闭 · Service Role Key 不进入浏览器 · 数据库权限由 RLS 强制执行</div></footer>
    </div>
  </div>;
}
